// Flattens every hotel + room across the site into a single registry the
// admin gallery page renders as a tree. New hotels/rooms are picked up
// automatically because we read from the same source the public site uses.

import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";

// Slugify a room. Accepts:
//   - a multilingual name object   { en, fr, es }
//   - a plain string
//   - a full room object           { slug?, name, camp?, ... }
// When a full room object carries an explicit `slug`, we honour it verbatim
// so that authors can pin unique identifiers across camps that share names
// (e.g. three "Haima Doble" rooms in three different camps).
export const slugifyRoom = (input) => {
  if (input && typeof input === "object" && typeof input.slug === "string" && input.slug) {
    return input.slug;
  }
  const source = input && typeof input === "object" && input.name ? input.name : input;
  const en = typeof source === "string" ? source : (source?.en || source?.fr || source?.es || "");
  return en
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "room";
};

// Build the [{hotel, rooms:[{slug,name,camp?,...}]}] tree from existing site
// data. The optional `camp` field on each room propagates through so the
// admin sidebar can render sub-groups (one per camp) for multi-camp
// properties (e.g. La Belle Étoile Bivouacs).
export const buildHotelRoomTree = () => {
  return HOTELS.map((h) => {
    const details = getHotelDetails(h.slug);
    const rooms = (details.rooms || []).map((r, idx) => {
      const slug = slugifyRoom(r) || `room-${idx + 1}`;
      return {
        slug,
        name: r.name,                // multilingual { en, fr, es }
        surface: r.surface || "",
        capacity: r.capacity || null,
        defaultImage: r.image || null,
        camp: r.camp || null,        // camp slug (e.g. "bivouac-la-belle-etoile") or null
        variantGroup: r.variantGroup || null,   // groups sibling rooms under one card
        variantLabel: r.variantLabel || null,   // tab label inside the parent card
      };
    });
    // Dedupe slug collisions
    const seen = new Map();
    rooms.forEach((r, i) => {
      if (seen.has(r.slug)) {
        r.slug = `${r.slug}-${i + 1}`;
      }
      seen.set(r.slug, true);
    });
    return {
      hotel: { slug: h.slug, name: h.name, category: h.category, location: h.location },
      rooms,
      camps: details.camps || [],   // pass camps through for admin sidebar grouping
    };
  });
};
